import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ReactTable from 'react-table';
import { Button } from 'reactstrap';
import { FaEye, FaExternalLinkAlt, FaColumns, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import NewPolicyDetails from '../policy-details/NewPolicyDetails';
// import ConsultationPaymentDetails from './ConsultationPaymentDetails';

// ─── Memoized Cell Components ────────────────────────────────────────────────

const IdCell = React.memo(function IdCell({ id, isVipUser, onView, onNewTab }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <Button type="submit" size="sm" className="bg-primary" onClick={onView}>
        {id}
      </Button>
      {!!isVipUser && (
        <span style={{
          backgroundColor: '#b78d78',
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 6px',
          borderRadius: '4px',
          color: '#fff',
          fontSize: '10px',
          fontWeight: 600,
        }}>VIP</span>
      )}
      {/* Action icons with lighter backgrounds */}
      <button
        type="button"
        title="View"
        onClick={onView}
        style={{
          background: '#eef2ff',
          border: '1px solid #c7d2fe',
          borderRadius: '6px',
          padding: '5px 7px',
          cursor: 'pointer',
          color: '#4f46e5',
          fontSize: '11px',
          boxShadow: '0 1px 3px rgba(79, 70, 229, 0.12)',
          display: 'inline-flex',
          alignItems: 'center',
          transition: 'transform 0.15s, box-shadow 0.15s, background 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.background = '#e0e7ff'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = '#eef2ff'; }}
      >
        <FaEye />
      </button>
      <button
        type="button"
        title="Open in new tab"
        onClick={onNewTab}
        style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          padding: '5px 7px',
          cursor: 'pointer',
          color: '#dc2626',
          fontSize: '11px',
          boxShadow: '0 1px 3px rgba(220, 38, 38, 0.1)',
          display: 'inline-flex',
          alignItems: 'center',
          transition: 'transform 0.15s, box-shadow 0.15s, background 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.background = '#fee2e2'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = '#fef2f2'; }}
      >
        <FaExternalLinkAlt />
      </button>
    </div>
  );
});

const BookedIdCell = React.memo(function BookedIdCell({ bookedConsultationId, onClick }) {
  if (!bookedConsultationId) return '';
  return (
    <Button type="submit" size="sm" className="bg-primary" onClick={onClick}>
      {bookedConsultationId}
    </Button>
  );
});

const CallsCell = React.memo(function CallsCell({ count, duration }) {
  return <p>{count} ({duration})</p>;
});

const VoiceCallbacksCell = React.memo(function VoiceCallbacksCell({
  totalConnectionRequest,
  attemptedCount,
  completedConnectionRequest,
  lastConnectionRequest,
}) {
  if (totalConnectionRequest > completedConnectionRequest) {
    return (
      <div className="text-danger">
        {completedConnectionRequest}/{totalConnectionRequest} - ({lastConnectionRequest})
        <p><strong>Attempts:</strong> {attemptedCount}</p>
      </div>
    );
  }
  if (totalConnectionRequest === 0) return <div>None</div>;
  return (
    <div className="text-success">
      {totalConnectionRequest} - ({lastConnectionRequest})
    </div>
  );
});

const VideoCallbacksCell = React.memo(function VideoCallbacksCell({
  totalVideoConnectionRequest,
  completedVideoConnectionRequest,
  lastVideoRequestTimestamp,
}) {
  if (totalVideoConnectionRequest > completedVideoConnectionRequest) {
    const date = new Date(lastVideoRequestTimestamp);
    const formatedDate = date.toDateString() + ', ' + date.toLocaleTimeString();
    return (
      <div className="text-danger">
        {completedVideoConnectionRequest}/{totalVideoConnectionRequest} - ({formatedDate})
      </div>
    );
  }
  if (totalVideoConnectionRequest === 0) return <div>None</div>;
  return (
    <div className="text-success">
      {totalVideoConnectionRequest} - ({lastVideoRequestTimestamp})
    </div>
  );
});

const RefundCell = React.memo(function RefundCell({
  refundId,
  refundStatus,
  refundReason,
  refundAmount,
  refundInitiatedAt,
  refundUpdatedAt,
}) {
  if (!refundId) return '';
  return (
    <div style={{ fontSize: '12px', lineHeight: '1.3' }}>
      <div>#{refundId} &middot; {refundStatus}</div>
      <div>&#8377;{refundAmount}</div>
      <div>{refundReason}</div>
      <div>{refundInitiatedAt}</div>
      <div>{refundUpdatedAt}</div>
    </div>
  );
});

// ─── Filter Options ──────────────────────────────────────────────────────────

const CANCEL_REASON_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'diff-doc', label: 'I want to consult a different doctor' },
  { value: 'change-slot', label: 'I want to change my time slot' },
  { value: 'consulted-elsewhere', label: 'Consulted somewhere else' },
  { value: 'not-needed', label: 'No longer needed' },
  { value: 'doc-unavailable', label: 'Booking attempt failed as Doctor is Unavailable' },
  { value: 'night-booking', label: 'Booking attempt failed as Night Booking is not allowed' },
  { value: 'active-consults', label: 'Booking attempt failed as Active Consultations found' },
  { value: 'payment-incomplete', label: 'Booking attempt failed as Payment not completed' },
  { value: 'telehealth-off', label: 'Booking attempt failed as Telehealth Services are Off Today' },
  { value: 'booking-failed', label: 'Booking attempt failed, check error msg' },
  { value: 'unknown-error', label: 'Booking attempt failed due to an Unknown Error' },
  { value: 'doctor-not-available', label: 'Consultation has cancelled as Doctor is Unavailable' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'cancelled-ops', label: 'Cancelled - Ops' },
  { value: 'cancelled-doctor', label: 'Cancelled - Doctor' },
  { value: 'cancelled-system', label: 'Cancelled - System' },
  { value: 'completed', label: 'Completed' },
  { value: 'ended', label: 'Ended' },
  { value: 'pending', label: 'Pending' },
  { value: 'user-not-reachable', label: 'User Not Reachable' },
  { value: 'doctor-never-connected', label: 'Doctor Never Connected' },
];

// ─── Column Builder ──────────────────────────────────────────────────────────

function buildColumns({
  consultationType,
  internalFilter,
  showConsultationView,
  openInNewTab,
}) {
  const isScheduled = consultationType === 'scheduled-consults';

  const columns = [
    {
      accessor: 'id',
      numeric: true,
      disablePadding: true,
      Header: isScheduled ? 'Consultation ID' : 'Consult ID',
      width: 180,
      style: { whiteSpace: 'unset' },
      filterable: true,
      Cell: (row) => {
        const { id, userId, isVipUser } = row.original;
        return (
          <IdCell
            id={id}
            isVipUser={isVipUser}
            onView={() => showConsultationView(id, consultationType, userId)}
            onNewTab={() => openInNewTab(id, consultationType, userId)}
          />
        );
      },
      Filter: ({ filter, onChange }) => (
        <input
          placeholder="Filter Ids"
          className="form-control"
          onChange={(e) => onChange(e.target.value)}
          value={filter ? filter.value : ''}
        />
      ),
    },
    {
      width: 200,
      accessor: 'scheduledDateTime',
      numeric: false,
      disablePadding: false,
      Header: isScheduled ? 'Scheduled At' : 'Scheduled Date Time',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 200,
      accessor: 'timeCreated',
      numeric: false,
      disablePadding: false,
      Header: isScheduled ? 'Created At' : 'Consultation Date',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 180,
      accessor: 'doctorName',
      numeric: false,
      disablePadding: false,
      Header: 'Doctor Name',
      style: { whiteSpace: 'unset' },
      Cell: (row) => {
        const { doctorName, assistantName, consultationType: rowType } = row.original;
        if (isScheduled) return doctorName;
        if (rowType !== 'online-sp') return assistantName;
        let name = doctorName;
        if (assistantName) {
          name += `, ${assistantName}`;
        }
        return name;
      },
    },
    {
      accessor: 'doctorVertical',
      numeric: false,
      disablePadding: false,
      Header: 'Vertical',
      width: 140,
      style: { whiteSpace: 'unset' },
      Cell: (row) => {
        const { doctorVertical, assistantVertical, consultationType: rowType } = row.original;
        if (rowType === 'doc-on-call') return assistantVertical;
        let name = doctorVertical;
        if (assistantVertical) {
          name += `, ${assistantVertical}`;
        }
        return name;
      },
    },
    {
      accessor: 'doctorId',
      numeric: false,
      sortable: false,
      disablePadding: false,
      Header: 'Doctor ID',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 180,
      accessor: 'userName',
      numeric: false,
      disablePadding: false,
      Header: 'User Name',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 180,
      accessor: 'patientName',
      numeric: false,
      disablePadding: false,
      Header: 'Patient Name',
      style: { whiteSpace: 'unset' },
    },
    {
      accessor: 'sponsorName',
      numeric: false,
      disablePadding: false,
      Header: 'Sponsor',
      style: { whiteSpace: 'unset', overflow: 'visible' },
    },
    {
      accessor: 'sponsorId',
      numeric: false,
      disablePadding: false,
      Header: 'Sponsor ID',
      style: { whiteSpace: 'unset', overflow: 'visible' },
    },
    {
      width: 200,
      accessor: 'cancelledOn',
      numeric: false,
      disablePadding: false,
      Header: 'Cancelled On',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 160,
      accessor: 'cancelledBy',
      numeric: true,
      disablePadding: false,
      Header: 'Cancelled By',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 260,
      accessor: 'cancelReason',
      numeric: false,
      disablePadding: false,
      Header: 'Cancel Reason',
      style: { whiteSpace: 'unset' },
      filterable: true,
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '100%' }}
          value={filter ? filter.value : (internalFilter && internalFilter.cancelReason) || ''}
        >
          {CANCEL_REASON_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ),
    },
    {
      width: 200,
      accessor: 'errorMessage',
      numeric: false,
      disablePadding: false,
      Header: 'Error Message',
      style: { whiteSpace: 'unset' },
    },
    {
      accessor: 'requestedCallType',
      numeric: false,
      disablePadding: false,
      Header: 'Call Type',
      style: { whiteSpace: 'unset', overflow: 'visible' },
    },
    {
      accessor: 'callsCount',
      numeric: false,
      sortable: false,
      disablePadding: false,
      Header: 'Calls (Duration)',
      style: { whiteSpace: 'unset' },
      Cell: (row) => (
        <CallsCell count={row.original.callsCount} duration={row.original.callsDuration} />
      ),
    },
    {
      accessor: 'videoCallCount',
      numeric: false,
      sortable: false,
      disablePadding: false,
      Header: 'Video Calls (Duration)',
      style: { whiteSpace: 'unset' },
      Cell: (row) => (
        <CallsCell count={row.original.videoCallCount} duration={row.original.videoCallDuration} />
      ),
    },
    {
      width: 80,
      accessor: 'tat',
      numeric: false,
      disablePadding: false,
      sortable: false,
      Header: 'TAT',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 80,
      accessor: 'opsTat',
      numeric: false,
      disablePadding: false,
      sortable: false,
      Header: 'Ops TAT',
      style: { whiteSpace: 'unset' },
    },
    {
      accessor: 'prescriptions',
      numeric: true,
      sortable: false,
      disablePadding: false,
      Header: 'Rx / Diet',
      filterable: true,
      style: { whiteSpace: 'unset' },
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(e) => onChange(e.target.value)}
          value={filter ? filter.value : (internalFilter && internalFilter.prescriptions) || ''}
        >
          <option value="all">All</option>
          <option value="sent">Sent</option>
          <option value="not-sent">Not Sent</option>
        </select>
      ),
    },
    {
      width: 120,
      accessor: 'rxTat',
      numeric: false,
      disablePadding: false,
      Header: 'Rx TAT (min)',
      style: { whiteSpace: 'unset' },
    },
    {
      accessor: 'doctorTTCount',
      numeric: false,
      sortable: false,
      disablePadding: false,
      Header: 'Dr. Msg',
      style: { whiteSpace: 'unset' },
    },
    {
      accessor: 'userTTCount',
      numeric: false,
      disablePadding: false,
      sortable: false,
      Header: 'User Msg',
      style: { whiteSpace: 'unset' },
    },
    {
      accessor: 'opsNote',
      numeric: false,
      sortable: false,
      disablePadding: false,
      Header: 'Notes',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 160,
      accessor: 'lastCommentTime',
      numeric: false,
      disablePadding: false,
      Header: 'Last Comment',
      style: { whiteSpace: 'unset' },
    },
    {
      accessor: 'amount',
      numeric: true,
      disablePadding: false,
      Header: 'Amount',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 180,
      accessor: 'status',
      numeric: false,
      disablePadding: false,
      sortable: false,
      Header: 'Status',
      style: { whiteSpace: 'unset' },
      filterable: true,
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(e) => onChange(e.target.value)}
          value={filter ? filter.value : (internalFilter && internalFilter.status) || ''}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ),
    },
    {
      width: 220,
      accessor: 'totalConnectionRequest',
      numeric: false,
      disablePadding: false,
      sortable: false,
      Header: 'Voice Callbacks',
      style: { whiteSpace: 'unset' },
      filterable: true,
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(e) => onChange(e.target.value)}
          value={filter ? filter.value : (internalFilter && internalFilter.connectionRequest) || ''}
        >
          <option value="All">All</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
        </select>
      ),
      Cell: (row) => (
        <VoiceCallbacksCell
          totalConnectionRequest={row.original.totalConnectionRequest}
          attemptedCount={row.original.attemptedCount}
          completedConnectionRequest={row.original.completedConnectionRequest}
          lastConnectionRequest={row.original.lastConnectionRequest}
        />
      ),
    },
    {
      width: 220,
      accessor: 'totalVideoConnectionRequest',
      numeric: false,
      disablePadding: false,
      sortable: false,
      Header: 'Video Callbacks',
      style: { whiteSpace: 'unset' },
      filterable: true,
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(e) => onChange(e.target.value)}
          value={filter ? filter.value : (internalFilter && internalFilter.videoConnectionRequest) || ''}
        >
          <option value="All">All</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
        </select>
      ),
      Cell: (row) => (
        <VideoCallbacksCell
          totalVideoConnectionRequest={row.original.totalVideoConnectionRequest}
          completedVideoConnectionRequest={row.original.completedVideoConnectionRequest}
          lastVideoRequestTimestamp={row.original.lastVideoRequestTimestamp}
        />
      ),
    },
    {
      width: 140,
      accessor: 'lastCallStatus',
      numeric: false,
      disablePadding: false,
      Header: 'Last Call Status',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 200,
      accessor: 'endedOn',
      numeric: true,
      disablePadding: false,
      Header: 'Ended On',
      style: { whiteSpace: 'unset' },
    },
    {
      width: 200,
      accessor: 'refund',
      numeric: false,
      sortable: false,
      disablePadding: false,
      Header: 'Refund',
      style: { whiteSpace: 'unset' },
      Cell: (row) => (
        <RefundCell
          refundId={row.original.refundId}
          refundStatus={row.original.refundStatus}
          refundReason={row.original.refundReason}
          refundAmount={row.original.refundAmount}
          refundInitiatedAt={row.original.refundInitiatedAt}
          refundUpdatedAt={row.original.refundUpdatedAt}
        />
      ),
    },
    {
      width: 180,
      accessor: 'bookedConsultationId',
      numeric: true,
      disablePadding: false,
      Header: isScheduled ? 'Booked ID' : 'Booked Consult ID',
      style: { whiteSpace: 'unset' },
      filterable: true,
      Cell: (row) => {
        const { bookedConsultationId, userId } = row.original;
        return (
          <BookedIdCell
            bookedConsultationId={bookedConsultationId}
            onClick={() => showConsultationView(bookedConsultationId, 'online-sp', userId)}
          />
        );
      },
      Filter: ({ filter, onChange }) => (
        <select
          onChange={(e) => onChange(e.target.value)}
          value={filter ? filter.value : (internalFilter && internalFilter.booked) || ''}
        >
          <option value="all">All</option>
          <option value="only-booked">Only Booked</option>
        </select>
      ),
    },
    {
      width: 180,
      accessor: 'consultationLanguage',
      numeric: true,
      disablePadding: false,
      Header: 'Language',
      style: { whiteSpace: 'unset' },
    },
  ];

  // Add Scheduled Consults filter column for online-sp type
  if (consultationType === 'online-sp') {
    const insertIdx = columns.findIndex((c) => c.accessor === 'tat');
    if (insertIdx !== -1) {
      columns.splice(insertIdx, 0, {
        accessor: 'scheduledAt',
        numeric: false,
        disablePadding: false,
        Header: 'Scheduled Consults',
        style: { whiteSpace: 'unset', overflow: 'visible' },
        filterable: true,
        Filter: ({ filter, onChange }) => (
          <select
            onChange={(e) => onChange(e.target.value)}
            style={{ width: '100%' }}
            value={filter ? filter.value : (internalFilter && internalFilter.schedule) || ''}
          >
            <option value="all">Show All</option>
            <option value="scheduled">Scheduled</option>
            <option value="not-scheduled">Not Scheduled</option>
          </select>
        ),
      });
    }
  }

  return columns;
}

// ─── Row Coloring ────────────────────────────────────────────────────────────

function getTrProps(state, rowInfo) {
  if (!rowInfo) return {};
  const row = rowInfo.original;
  if (row.hasScheduleConsultId === 1) return { style: { background: '#f7c42b' } };
  if (row.hasScheduleConsultId === 0 && row.requestedCallType === 'video') return { style: { background: '#FFC0CB' } };
  if (row.platform === 'OPS-ABHI') return { style: { background: '#FF4D4D' } };
  if (row.source === 'VISIT-PHARMACY') return { style: { background: '#838bc8' } };
  if (row.platform === 'HOTLINE') return { style: { background: '#489652' } };
  return {};
}

function getTheadFilterThProps() {
  return { style: { overflow: 'visible' } };
}

function getTheadThProps() {
  return { style: { background: '#f8fafc', borderRight: '1px solid #eef2f7' } };
}

// ─── Columns Popover ─────────────────────────────────────────────────────────

function ColumnsPopover({ allColumns, visibleAccessors, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          fontSize: '12px',
          padding: '6px 12px',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          background: open ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff',
          color: open ? '#fff' : '#4a5568',
          cursor: 'pointer',
          fontWeight: 500,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'all 0.2s',
        }}
      >
        <FaColumns size={11} /> Columns
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
            padding: '12px 14px',
            zIndex: 30,
            width: '260px',
            maxHeight: '340px',
            overflowY: 'auto',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Toggle Columns
            </span>
            <button
              type="button"
              onClick={onReset}
              style={{
                fontSize: '11px',
                background: 'none',
                border: 'none',
                color: '#667eea',
                cursor: 'pointer',
                padding: 0,
                fontWeight: 600,
              }}
            >
              Show All
            </button>
          </div>
          {allColumns.map((col) => (
            <label
              key={col.accessor}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                padding: '4px 2px',
                cursor: 'pointer',
                margin: 0,
                borderRadius: '4px',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <input
                type="checkbox"
                checked={visibleAccessors.includes(col.accessor)}
                onChange={() => onToggle(col.accessor)}
                style={{ accentColor: '#667eea' }}
              />
              <span style={{ color: '#334155' }}>
                {typeof col.Header === 'string' ? col.Header : col.accessor}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

function DocOnCallTableV2Inner({
  allowedColumns,
  showConsultationView,
  onFetchData,
  loading,
  data,
  pageSize,
  pages,
  pageIndex,
  internalFilter,
  consultationType,
  visibleColumns,
  onVisibleColumnsChange,
  openInNewTab,
  fetchUserAndPatientTableData,
}) {
  const baseColumns = useMemo(
    () => {
      const allCols = buildColumns({
        consultationType,
        internalFilter,
        showConsultationView,
        openInNewTab,
      });
      return allCols.filter((c) => allowedColumns.includes(c.accessor));
    },
    [consultationType, internalFilter, showConsultationView, openInNewTab, allowedColumns]
  );

  const toggleableColumns = useMemo(
    () => baseColumns,
    [baseColumns]
  );

  const effectiveVisible = useMemo(() => {
    if (visibleColumns && Array.isArray(visibleColumns)) return visibleColumns;
    return toggleableColumns.map((c) => c.accessor);
  }, [visibleColumns, toggleableColumns]);

  const columnsForTable = useMemo(() => {
    return baseColumns.map((c) => ({
      ...c,
      show: effectiveVisible.includes(c.accessor),
    }));
  }, [baseColumns, effectiveVisible]);

  const toggleColumn = useCallback(
    (accessor) => {
      const next = effectiveVisible.includes(accessor)
        ? effectiveVisible.filter((a) => a !== accessor)
        : [...effectiveVisible, accessor];
      onVisibleColumnsChange(next);
    },
    [effectiveVisible, onVisibleColumnsChange]
  );

  const resetColumns = useCallback(() => {
    onVisibleColumnsChange(null);
  }, [onVisibleColumnsChange]);

  // Custom pagination component — premium toolbar
  const PaginationComponent = useCallback(({ page: pgPage, pages: pgPages, pageSize: pgPageSize, onPageChange, onPageSizeChange, canPrevious, canNext }) => {
    const PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 50, 100, 300, 500, 800, 1000, 2000];
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 14px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          borderRadius: '10px 10px 0 0',
          flexWrap: 'wrap',
        }}
      >
        {/* Rows per page */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>Show</span>
          <select
            value={pgPageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            style={{
              height: '30px',
              padding: '2px 8px',
              fontSize: '12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#334155',
              fontWeight: 500,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              cursor: 'pointer',
            }}
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>rows</span>
        </div>

        {/* Divider */}
        <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }} />

        {/* Previous */}
        <button
          type="button"
          disabled={!canPrevious}
          onClick={() => onPageChange(pgPage - 1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '5px 10px',
            fontSize: '12px',
            fontWeight: 500,
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            background: canPrevious ? '#fff' : '#f8fafc',
            color: canPrevious ? '#334155' : '#94a3b8',
            cursor: canPrevious ? 'pointer' : 'not-allowed',
            boxShadow: canPrevious ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            transition: 'all 0.15s',
          }}
        >
          <FaChevronLeft size={9} /> Prev
        </button>

        {/* Page info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
          <span>Page</span>
          <input
            type="number"
            min={1}
            max={pgPages || 1}
            value={pgPage + 1}
            onChange={(e) => {
              const val = Number(e.target.value) - 1;
              if (val >= 0 && val < (pgPages || 1)) onPageChange(val);
            }}
            style={{
              width: '44px',
              height: '28px',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              padding: '2px 4px',
              color: '#334155',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          />
          <span>of <strong style={{ color: '#334155' }}>{pgPages || 1}</strong></span>
        </div>

        {/* Next */}
        <button
          type="button"
          disabled={!canNext}
          onClick={() => onPageChange(pgPage + 1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '5px 10px',
            fontSize: '12px',
            fontWeight: 500,
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            background: canNext ? '#fff' : '#f8fafc',
            color: canNext ? '#334155' : '#94a3b8',
            cursor: canNext ? 'pointer' : 'not-allowed',
            boxShadow: canNext ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            transition: 'all 0.15s',
          }}
        >
          Next <FaChevronRight size={9} />
        </button>

        {/* Divider */}
        <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }} />

        {/* Refresh */}
        <button
          type="button"
          onClick={fetchUserAndPatientTableData}
          title="Refresh"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '12px',
            padding: '6px 12px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            background: '#fff',
            color: '#4a5568',
            cursor: 'pointer',
            fontWeight: 500,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            transition: 'all 0.2s',
          }}
        >
          <span style={{ fontSize: '14px', lineHeight: 1 }}>↻</span> Refresh
        </button>

        {/* Columns toggle */}
        <ColumnsPopover
          allColumns={toggleableColumns}
          visibleAccessors={effectiveVisible}
          onToggle={toggleColumn}
          onReset={resetColumns}
        />
      </div>
    );
  }, [toggleableColumns, effectiveVisible, toggleColumn, resetColumns, fetchUserAndPatientTableData]);

  return (
    <ReactTable
      data={data}
      columns={columnsForTable}
      loading={loading}
      pages={pages}
      manual
      onFetchData={onFetchData}
      showPaginationTop={true}
      showPaginationBottom={false}
      PaginationComponent={PaginationComponent}
      className="-striped -highlight"
      defaultPageSize={pageSize}
      page={pageIndex}
      getTrProps={getTrProps}
      pageSizeOptions={[5, 10, 20, 25, 50, 100, 300, 500, 800, 1000, 2000]}
      defaultSorted={[{ id: 'id', desc: true }]}
      getTheadFilterThProps={getTheadFilterThProps}
      getTheadThProps={getTheadThProps}
      SubComponent={(row) => (
        <div className="policy-subrow-compact" style={{ padding: '4px 8px', background: '#fafbfc', borderTop: '1px solid #e2e8f0' }}>
          <style>{`
            .policy-subrow-compact .container.card {
              max-width: 1000px !important;
              padding: 6px 10px !important;
              margin: 0 !important;
            }
          `}</style>
          {/* <ConsultationPaymentDetails consultationId={row.original.id} type={consultationType === 'scheduled-consults' ? 'scheduled' : 'consultation'} /> */}
          <NewPolicyDetails userId={row.original.userId} tUserId={row.original.tUserId || null} />
        </div>
      )}
    />
  );
}

const DocOnCallTableV2 = React.memo(DocOnCallTableV2Inner);
export default DocOnCallTableV2;
